/**
 * usePipelineManager — thin facade composing pipeline save and CRUD hooks.
 *
 * Owns shared state (loaded semantic type, sorted pipelines) and delegates
 * save/update flows to usePipelineSave and open/edit/delete flows to
 * usePipelineCrud. EditorPage consumes only this facade.
 */

import React from 'react';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ISavedPipeline } from '@/interfaces/IStorage';
import { useEditorStore } from '@/store/useEditorStore';
import { usePipelineStore } from '@/store/usePipelineStore';

import { usePipelineCrud } from './usePipelineCrud';
import { usePipelineSave } from './usePipelineSave';

/* ------------------------------------------------------------------ */
/*  Dialog prop shapes (match popup component interfaces exactly)     */
/* ------------------------------------------------------------------ */

export interface SaveDialogProps {
  open: boolean;
  name: string;
  description: string;
  error: string | null;
  canSave: boolean;
  includeInputData: boolean;
  inputDataSize: number;
  mode: 'create' | 'update';
  onChangeName: (v: string) => void;
  onChangeDescription: (v: string) => void;
  onChangeIncludeInputData: (v: boolean) => void;
  onClose: () => void;
  onSave: () => void;
  onSaveAsNew: () => void;
}

export interface SaveChoiceDialogProps {
  open: boolean;
  pipelineName: string | null;
  onCancel: () => void;
  onUpdateExisting: () => void;
  onSaveAsNew: () => void;
}

export interface EditDialogProps {
  open: boolean;
  name: string;
  description: string;
  error: string | null;
  onChangeName: (v: string) => void;
  onChangeDescription: (v: string) => void;
  onClose: () => void;
  onSave: () => void;
}

export interface DeleteDialogProps {
  open: boolean;
  pipeline: ISavedPipeline | null;
  onCancel: () => void;
  onDelete: () => void;
}

/* ------------------------------------------------------------------ */
/*  Options                                                           */
/* ------------------------------------------------------------------ */

interface UsePipelineManagerOptions {
  /** Called when a pipeline action needs to clear the step-settings panel. */
  resetStepSelection: () => void;
  /** Called when opening a loaded pipeline should clear the current tool selection. */
  clearToolSelection: () => void;
}

/* ------------------------------------------------------------------ */
/*  Return type                                                       */
/* ------------------------------------------------------------------ */

export interface UsePipelineManagerResult {
  /** Pipelines sorted newest-first. */
  sortedPipelines: ISavedPipeline[];
  /** Semantic type of the output from the last loaded pipeline (hint for recommendations). */
  loadedPipelineOutputType: ISemanticType | null;
  /** Load a saved pipeline into the editor. */
  openPipeline: (pipeline: ISavedPipeline) => void;
  /** Open the edit-metadata dialog for a pipeline. */
  openEditPipeline: (pipeline: ISavedPipeline) => void;
  /** Open the delete-confirmation dialog for a pipeline. */
  openDeletePipeline: (pipeline: ISavedPipeline) => void;
  /** Trigger the "save pipeline" flow (choice dialog if already saved, else new-save). */
  openSavePipeline: () => void;
  /** Props to spread directly onto <SavePipelinePopup />. */
  saveDialogProps: SaveDialogProps;
  /** Props to spread directly onto <SavePipelineChoicePopup />. */
  saveChoiceDialogProps: SaveChoiceDialogProps;
  /** Props to spread directly onto <EditPipelinePopup />. */
  editDialogProps: EditDialogProps;
  /** Props to spread directly onto <DeletePipelinePopup />. */
  deleteDialogProps: DeleteDialogProps;
}

/* ------------------------------------------------------------------ */
/*  Hook implementation                                               */
/* ------------------------------------------------------------------ */

export function usePipelineManager(options: UsePipelineManagerOptions): UsePipelineManagerResult {
  const { resetStepSelection, clearToolSelection } = options;

  /* ---------- shared state ---------- */
  const savedPipelines = usePipelineStore((s) => s.savedPipelines);
  const pipelineLength = useEditorStore((s) => s.pipeline.length);
  const [loadedOutputSemanticType, setLoadedOutputSemanticType] = React.useState<string | null>(null);

  const semanticTypeByName = React.useCallback(
    (name: string | null | undefined): ISemanticType | null => {
      if (!name) return null;
      const match = Object.values(SEMANTIC_TYPES).find((t) => t.semanticType === name);
      return match ?? null;
    },
    []
  );

  const loadedPipelineOutputType = React.useMemo(
    () => semanticTypeByName(loadedOutputSemanticType),
    [loadedOutputSemanticType, semanticTypeByName]
  );

  const sortedPipelines = React.useMemo(
    () => [...savedPipelines].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [savedPipelines]
  );

  // Clear loaded semantic type when pipeline is emptied.
  React.useEffect(() => {
    if (pipelineLength === 0) setLoadedOutputSemanticType(null);
  }, [pipelineLength]);

  /* ---------- delegate to sub-hooks ---------- */
  const { openSavePipeline, saveDialogProps, saveChoiceDialogProps } = usePipelineSave(
    setLoadedOutputSemanticType,
    loadedPipelineOutputType,
    resetStepSelection,
  );

  const { openPipeline, openEditPipeline, openDeletePipeline, editDialogProps, deleteDialogProps } = usePipelineCrud(
    setLoadedOutputSemanticType,
    semanticTypeByName,
    resetStepSelection,
    clearToolSelection,
  );

  return {
    sortedPipelines,
    loadedPipelineOutputType,
    openPipeline,
    openEditPipeline,
    openDeletePipeline,
    openSavePipeline,
    saveDialogProps,
    saveChoiceDialogProps,
    editDialogProps,
    deleteDialogProps,
  };
}
